import * as KatalMetrics from '@amzn/katal-metrics';
import initialMetricsPublisher from "src/katalMetrics/metrics";
import {SITE_SERVICES, METRIC_CONFIG, METRIC_TYPE, ACTION_METHODS} from './metricsModels';
import Bowser from "bowser";
import corpReportingAppSettings from "src/Ateam-BI/CorpReporting/app_settings";

/* Our Portal has many services, the below helper method is to tag right service based on the path provided 
(Below method is used when capturing PAGE_VISIT metric from a generic place like AppRouter)

Rules:
1. All home page paths -  ("/") or ("/Home") or contains ("/org") etc is tagged to "CLOSE_PORTAL" Service - Including the "Apps Library" tab on home page
2. All Boffin paths are tagged to "BOFFIN" service
3. All TED paths are tagged to "TED" service
4. All CorpReporting paths are tagged to "CORPORATE_REPORTING_CENTRAL" service
5. All FRISCO paths are tagged to "FRISCO" service
6. All LOGICO paths are tagged to "LOGICO" service
7. All FLEXICO paths are tagged to "FLEXICO" service
8. All Admin paths are tagged to "ADMIN_PANNEL" service
9. All Approval paths are tagged to "APPROVAL_SERVICE" service 
10.All App page paths are tagged to "ACCOUNTING_CENTRAL_APP" service 
11.If no above rules were applicable, metric is tagged to general "ACCOUNTING_BI_CENTRAL" service

Please include appropriate rule below and in right order, when adding new path to AppRouter such that PAGE_VISIT metric is tagged to right service

*/
export const getServiceForMetricsByPath = (path = "") => {

    // Please note order of these rules are important 

    // Check if path belongs to corp reporting app (by app ID) - must be before individual service checks
    const corpReportingAppMatch = path.match(/^\/App\/(\d+)/i);
    if (corpReportingAppMatch) {
        const appId = corpReportingAppMatch[1];
        const corpReportingAppIds = Object.values(corpReportingAppSettings)
            .filter((s: any) => s?.appId)
            .map((s: any) => s.appId);
        if (corpReportingAppIds.includes(appId)) {
            return SITE_SERVICES.CORPORATE_REPORTING_CENTRAL
        }
    }

    if(path?.toLowerCase()?.includes("boffin")) {
        return SITE_SERVICES.BOFFIN
    } else if (path?.toLowerCase()?.includes("corpreporting")) {
        return SITE_SERVICES.CORPORATE_REPORTING_CENTRAL
    } else if (path?.toLowerCase()?.includes("tedhome")
            || path?.toLowerCase()?.includes("triggereventlogs")
            || path?.toLowerCase()?.includes("triggerhistory")
            || path?.toLowerCase()?.includes("emailsubscription")
            || path?.toLowerCase()?.includes("entityregionmapping")
            || path?.toLowerCase()?.includes("regiontriggermapping")) {
        return SITE_SERVICES.TED
    } else if (path?.toLowerCase()?.includes("approvalshome")) {
        return SITE_SERVICES.APPROVAL_SERVICE
    } else if (path?.toLowerCase()?.includes("admin")) {
        return SITE_SERVICES.ADMIN_PANEL
    } else if (path?.toLowerCase()?.includes("auditlogs")) {
        return SITE_SERVICES.AUDIT_LOGS
    } else if (path?.toLowerCase()?.includes("logico")) {
        return SITE_SERVICES.LOGICO
    } else if (path?.toLowerCase()?.includes("flexico")) {
        return SITE_SERVICES.FLEXICO
    } else if (path?.toLowerCase()?.includes("firstpass")) {
        return SITE_SERVICES.FIRST_PASS_PRO
    } else if (path?.toLowerCase()?.includes("frisco") 
            || path?.toLowerCase()?.includes("process")) {
        return SITE_SERVICES.FRISCO
    } else if (path?.toLowerCase()?.includes("appslibrary")) {
        return SITE_SERVICES.APPS_LIBRARY
    } else if (path?.toLowerCase()?.includes("app")) {
        return SITE_SERVICES.ACCOUNTING_CENTRAL_APP
    } else if (path === "/" 
            || path?.toLowerCase() === "/home" 
            || path?.toLowerCase() === "/ateambihome"
            || path?.toLowerCase() === "/ateambiallproducts"
            || path?.toLowerCase()?.includes("/closeportal")) {
        return SITE_SERVICES.CLOSE_PORTAL
    }
     else {
        return SITE_SERVICES.ACCOUNTING_BI_CENTRAL
    }
}
//[{metricType,metricName, metricValue, isMonitor}]
export const captureMetrics = (actionMethod: string, metrics:Array<METRIC_CONFIG>, service:string = SITE_SERVICES.ACCOUNTING_BI_CENTRAL) => {
        
    const actionMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(actionMethod,new KatalMetrics.Context.Builder()
            .withServiceName(service)
            .build())

    metrics.forEach((metric) => {
        if(metric?.metricType === METRIC_TYPE.COUNTER) {
            const counterMetric = new KatalMetrics.Metric.Counter(metric?.metricName, typeof(metric?.metricValue) === 'number' ? metric?.metricValue: 1).withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(counterMetric)
        } else if (metric?.metricType === METRIC_TYPE.STRING) {
            const stringMetricc = new KatalMetrics.Metric.String(metric?.metricName, typeof(metric?.metricValue) === 'string' ? metric?.metricValue: '').withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(stringMetricc)
        } else if (metric?.metricType === METRIC_TYPE.TIMER) {
            const timerMetric = new KatalMetrics.Metric.Timer(metric?.metricName, typeof(metric?.metricValue) === 'number' ? metric?.metricValue: 1).withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(timerMetric)
        }
    })

    return actionMetricPublisher;
    
}

export const createKatalTimerStopWatch = (timerStopwatchMetricName: string) => {
    
    const timerStopwatchMetric = new KatalMetrics.Metric.TimerStopwatch(timerStopwatchMetricName).withMonitor()
    return timerStopwatchMetric

}

export const publishTimerStopwatchMetric = (timerStopwatchMetric: KatalMetrics.Metric.TimerStopwatch, actionMethod: string, service:string = SITE_SERVICES.ACCOUNTING_BI_CENTRAL) => {
    const actionMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(actionMethod,new KatalMetrics.Context.Builder()
                .withServiceName(service)
                .build())

    actionMetricPublisher.publish(timerStopwatchMetric)
}

export const stopAndCaptureTimeMetricsWithBasicAttributes = (timerStopwatchMetric: KatalMetrics.Metric.TimerStopwatch, userAlias: string,pagePath:string,actionMethod: string, service:string, customMetrics = []) => {
    timerStopwatchMetric.stop()
    const browser = Bowser.getParser(window.navigator.userAgent);    
    const actionMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod(actionMethod, new KatalMetrics.Context.Builder()
            .withServiceName(service)
            .build())

    const metrics = [{
            metricName : 'actionPerformedBy',
            metricValue : userAlias,
            metricType : METRIC_TYPE.STRING,
            isMonitor : true
        },
        {
            metricName : 'pagePath',
            metricValue : pagePath,
            metricType : METRIC_TYPE.STRING,
            isMonitor : true
        },
        {
            metricName : 'browserName',
            metricValue : browser.getBrowserName(),
            metricType : METRIC_TYPE.STRING,
            isMonitor : true
        },
        {
            metricName : 'operatingSystem',
            metricValue : browser.getOSName(),
            metricType : METRIC_TYPE.STRING,
            isMonitor : true
        },
        ].concat(customMetrics)
    metrics.forEach((metric) => {
        if(metric?.metricType === METRIC_TYPE.COUNTER) {
            const counterMetric = new KatalMetrics.Metric.Counter(metric?.metricName, typeof(metric?.metricValue) === 'number' ? metric?.metricValue: 1).withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(counterMetric)
        } else if (metric?.metricType === METRIC_TYPE.STRING) {
            const stringMetricc = new KatalMetrics.Metric.String(metric?.metricName, typeof(metric?.metricValue) === 'string' ? metric?.metricValue: '').withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(stringMetricc)
        } else if (metric?.metricType === METRIC_TYPE.TIMER) {
            const timerMetric = new KatalMetrics.Metric.Timer(metric?.metricName, typeof(metric?.metricValue) === 'number' ? metric?.metricValue: 1).withMonitor(metric.isMonitor)
            actionMetricPublisher.publish(timerMetric)
        }
    })
    actionMetricPublisher.publish(timerStopwatchMetric)    
}